import React from 'react';
import { connect } from 'react-redux';

import {
  workflowSetCreatorFullAccess,
  workflowSetCreatorPermissions,
  workflowSetFullAccessPermissions,
} from '~/actions/workflow';
import UserDepartmentSelect from '~/components/Shared/UserDepartmentSelect/UserDepartmentSelect';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { TicketStageType } from '~/enums/TicketStageType';
import { WorkflowCreatorPermissionType } from '~/enums/WorkflowCreatorPermissionType';
import { WorkflowUserDepartmentInclusionType } from '~/enums/WorkflowUserDepartmentInclusionType';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '~/ui/components/Box/Box';
import Card from '~/ui/components/Card/index';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import RadioGroup from '~/ui/components/RadioGroup/RadioGroup';
import Text from '~/ui/components/Text/Text';
import {
  creatorPermissionEnums,
  getEditOptions,
  getFinalizeOptions,
  getReviewOptions,
  getSignOptions,
  getTicketOptions,
} from '~/utils/workflow';

import Section from '../Section';

const testIsStageEnabled = (stage) =>
  stage.isEnabled || Boolean(stage.conditionRule);

export const testIsTicketPermissionsEnabled = ({ stages }) =>
  !Object.values(stages).some(testIsStageEnabled);

export const testIsStagePermissionsEnabled = ({ stage, stages }) =>
  testIsStageEnabled(stages[stage]);

function Permissions({
  // connected
  creatorFullAccess,
  creatorPermissions,
  fullAccessPermissions,
  stages,
  workflowSetCreatorFullAccess,
  workflowSetCreatorPermissions,
  workflowSetFullAccessPermissions,
  wssDesignated,
}) {
  const intl = useIntl();
  const reviewOptions = getReviewOptions(intl);
  const editOptions = getEditOptions(intl);
  const finalizeOptions = getFinalizeOptions(intl);
  const signOptions = getSignOptions(intl);
  const ticketOptions = getTicketOptions(intl);

  const accessOptions = [
    {
      label: intl.formatMessage({
        id: 'Permissions.fullAccessLabel',
        defaultMessage: 'Full Access',
        description: 'Label for full access option',
      }),
      value: true,
    },
    {
      label: intl.formatMessage({
        id: 'Permissions.limitedAccessLabel',
        defaultMessage: 'Limited Access',
        description: 'Label for limited access option',
      }),
      value: false,
    },
  ];

  const userDeptOptions = [
    {
      label: intl.formatMessage({
        id: 'Permissions.allUsersLabel',
        defaultMessage: 'All users',
        description: 'Label for all users option',
      }),
      value: WorkflowUserDepartmentInclusionType.All,
    },
    {
      label: intl.formatMessage({
        id: 'Permissions.allUsersExceptLabel',
        defaultMessage: 'All users except',
        description: 'Label for all users except option',
      }),
      value: WorkflowUserDepartmentInclusionType.Exclude,
    },
    {
      label: intl.formatMessage({
        id: 'Permissions.onlyLabel',
        defaultMessage: 'Only',
        description: 'Label for only specific users option',
      }),
      value: WorkflowUserDepartmentInclusionType.Include,
    },
  ];

  function handleUpdateOptions(stage, values) {
    workflowSetCreatorPermissions({ stage, values });
  }

  function toggleViewApproval(current, updated) {
    const basicEnum = creatorPermissionEnums.VIEW_APPROVAL_BASIC;
    const detailedEnum = creatorPermissionEnums.VIEW_APPROVAL_DETAILED;

    if (current.includes(detailedEnum) && updated.includes(basicEnum)) {
      return updated.filter((v) => v !== detailedEnum);
    }

    if (current.includes(basicEnum) && updated.includes(detailedEnum)) {
      return updated.filter((v) => v !== basicEnum);
    }

    return updated;
  }

  function renderEditPermissionsSelect() {
    const permissionKey = WorkflowCreatorPermissionType.Edit;
    const currentPermissions = creatorPermissions[permissionKey];
    return (
      <Select
        enablePortal
        isMulti={true}
        options={editOptions}
        placeholder={intl.formatMessage({
          id: 'Permissions.specifyEditPermissionsPlaceholder',
          defaultMessage: 'Specify Edit permissions',
          description: 'Placeholder for edit permissions select',
        })}
        value={currentPermissions}
        width="100%"
        onChange={(updatedValues) => {
          return handleUpdateOptions(permissionKey, updatedValues);
        }}
      />
    );
  }

  function renderReviewPermissionsSelect() {
    const permissionKey = WorkflowCreatorPermissionType.Review;
    const currentPermissions = creatorPermissions[permissionKey];
    return (
      <Select
        enablePortal
        isMulti={true}
        options={reviewOptions}
        placeholder={intl.formatMessage({
          id: 'Permissions.specifyReviewPermissionsPlaceholder',
          defaultMessage: 'Specify Review permissions',
          description: 'Placeholder for review permissions select',
        })}
        value={currentPermissions}
        width="100%"
        onChange={(updatedValues) => {
          updatedValues = toggleViewApproval(currentPermissions, updatedValues);
          return handleUpdateOptions(permissionKey, updatedValues);
        }}
      />
    );
  }

  function renderSignPermissionsSelect() {
    const permissionKey = WorkflowCreatorPermissionType.Sign;
    const currentPermissions = creatorPermissions[permissionKey];
    return (
      <Select
        enablePortal
        isMulti={true}
        options={signOptions}
        placeholder={intl.formatMessage({
          id: 'Permissions.specifySignPermissionsPlaceholder',
          defaultMessage: 'Specify Sign permissions',
          description: 'Placeholder for sign permissions select',
        })}
        value={currentPermissions}
        width="100%"
        onChange={(updatedValues) => {
          updatedValues = toggleViewApproval(currentPermissions, updatedValues);
          return handleUpdateOptions(permissionKey, updatedValues);
        }}
      />
    );
  }

  function renderFinalizePermissionsSelect() {
    const permissionKey = WorkflowCreatorPermissionType.Finalize;
    const currentPermissions = creatorPermissions[permissionKey];
    return (
      <Select
        enablePortal
        isMulti={true}
        options={finalizeOptions}
        placeholder={intl.formatMessage({
          id: 'Permissions.specifyFinalizePermissionsPlaceholder',
          defaultMessage: 'Specify Finalize permissions',
          description: 'Placeholder for finalize permissions select',
        })}
        value={currentPermissions}
        width="100%"
        onChange={(updatedValues) => {
          updatedValues = toggleViewApproval(currentPermissions, updatedValues);
          return handleUpdateOptions(permissionKey, updatedValues);
        }}
      />
    );
  }

  function renderTicketPermissionSelect() {
    const permissionKey = WorkflowCreatorPermissionType.Ticket;
    const currentPermissions = creatorPermissions[permissionKey];
    return (
      <Select
        enablePortal
        isMulti={true}
        options={ticketOptions}
        placeholder={intl.formatMessage({
          id: 'Permissions.specifyTicketPermissionsPlaceholder',
          defaultMessage: 'Specify Ticket permissions',
          description: 'Placeholder for ticket permissions select',
        })}
        value={currentPermissions}
        width="100%"
        onChange={(updatedValues) => {
          updatedValues = toggleViewApproval(currentPermissions, updatedValues);
          return handleUpdateOptions('ticket', updatedValues);
        }}
      />
    );
  }

  return (
    <Section
      description={intl.formatMessage({
        id: 'Permissions.sectionDescription',
        defaultMessage:
          'Manage workflow access and limit what ticket submitters can see on a created ticket.',
        description: 'Description for permissions section',
      })}
      icon="briefcase"
      id="workflow--settings-permissions-header"
      title={intl.formatMessage({
        id: 'Permissions.sectionTitle',
        defaultMessage: 'Permissions',
        description: 'Title for permissions section',
      })}
    >
      <FlexLayout space={6} py={2} flexDirection="column">
        <Text variant="xs-dense-caps">
          <FormattedMessage
            id="Permissions.generalAccessHeading"
            defaultMessage="General Access"
            description="Heading for general access section"
          />
        </Text>
        <Card
          id="workflow--settings-see-submitted-tickets-header"
          header={
            <Card.Header
              title={intl.formatMessage({
                id: 'Permissions.seeSubmittedTicketsTitle',
                defaultMessage: 'See Submitted Tickets',
                description: 'Title for see submitted tickets card',
              })}
            />
          }
          onToggleCollapse={() => {}}
        >
          <Text color="gray-600">
            <FormattedMessage
              id="Permissions.specifyUsersDescription"
              defaultMessage="Specify users and departments who will be able to see tickets created with this workflow."
              description="Description for specifying users and departments"
            />
          </Text>
          <Layout spacing={4} py={4}>
            <Select
              enablePortal
              isClearable={false}
              id="workflow--settings-see-submitted-tickets-select"
              onChange={(rule) => {
                workflowSetFullAccessPermissions({
                  ...fullAccessPermissions,
                  rule,
                });
              }}
              options={userDeptOptions}
              value={fullAccessPermissions.rule}
              width="input.s.width"
            />
            {!(
              fullAccessPermissions.rule ===
              WorkflowUserDepartmentInclusionType.All
            ) && (
              <UserDepartmentSelect
                enablePortal
                value={fullAccessPermissions.userDepartments}
                onChange={(userDepartments) => {
                  workflowSetFullAccessPermissions({
                    ...fullAccessPermissions,
                    userDepartments,
                  });
                }}
              />
            )}
          </Layout>
        </Card>
      </FlexLayout>
      <FlexLayout flexDirection="column" space={4} py={2}>
        <Box id="workflow--settings-submitter-access-header">
          <Text variant="xs-dense-caps">
            <FormattedMessage
              id="Permissions.submitterAccessHeading"
              defaultMessage="Submitter Access"
              description="Heading for submitter access section"
            />
          </Text>
        </Box>
        <Text variant="xs-dense" color="gray-600">
          <FormattedMessage
            id="Permissions.submitterAccessDescription"
            defaultMessage="Control the details of what a ticket submitter is able to see and do after they submit a request based on this workflow."
            description="Description for submitter access controls"
          />
        </Text>
        <RadioGroup
          id="workflow--settings-submitter-access-full-access"
          onChange={(value) => workflowSetCreatorFullAccess(value)}
          options={accessOptions}
          value={creatorFullAccess}
          disabled={wssDesignated}
        />
        {!creatorFullAccess && (
          <>
            <Card
              header={
                <Card.Header
                  title={intl.formatMessage({
                    id: 'Permissions.submitterAccessCardTitle',
                    defaultMessage: 'Submitter Access',
                    description: 'Title for submitter access card',
                  })}
                  onToggleCollapse={() => {}}
                />
              }
            >
              <FlexLayout flexDirection="column" space={6}>
                {testIsStagePermissionsEnabled({
                  stage: TicketStageType.Edit,
                  stages,
                }) && (
                  <Box sx={{ width: '100%' }}>
                    <Text variant="xs-dense-bold">
                      <FormattedMessage
                        id="Permissions.editStageLabel"
                        defaultMessage="Edit"
                        description="Label for edit stage permissions"
                      />
                    </Text>
                    <Box mt={2}>{renderEditPermissionsSelect()}</Box>
                  </Box>
                )}
                {testIsStagePermissionsEnabled({
                  stage: TicketStageType.Review,
                  stages,
                }) && (
                  <Box sx={{ width: '100%' }}>
                    <Text variant="xs-dense-bold">
                      <FormattedMessage
                        id="Permissions.reviewStageLabel"
                        defaultMessage="Review"
                        description="Label for review stage permissions"
                      />
                    </Text>
                    <Box mt={2}>{renderReviewPermissionsSelect()}</Box>
                  </Box>
                )}
                {testIsStagePermissionsEnabled({
                  stage: TicketStageType.Sign,
                  stages,
                }) && (
                  <Box sx={{ width: '100%' }}>
                    <Text variant="xs-dense-bold">
                      <FormattedMessage
                        id="Permissions.signStageLabel"
                        defaultMessage="Sign"
                        description="Label for sign stage permissions"
                      />
                    </Text>
                    <Box mt={2}>{renderSignPermissionsSelect()}</Box>
                  </Box>
                )}
                {testIsStagePermissionsEnabled({
                  stage: TicketStageType.Finalize,
                  stages,
                }) && (
                  <Box sx={{ width: '100%' }}>
                    <Text variant="xs-dense-bold">
                      <FormattedMessage
                        id="Permissions.finalizeStageLabel"
                        defaultMessage="Finalize"
                        description="Label for finalize stage permissions"
                      />
                    </Text>
                    <Box mt={2}>{renderFinalizePermissionsSelect()}</Box>
                  </Box>
                )}
                {testIsTicketPermissionsEnabled({ stages }) && (
                  <Box sx={{ width: '100%' }}>
                    <Text variant="xs-dense-bold">
                      <FormattedMessage
                        id="Permissions.ticketStageLabel"
                        defaultMessage="Ticket"
                        description="Label for ticket stage permissions"
                      />
                    </Text>
                    <Box mt={2}>{renderTicketPermissionSelect()}</Box>
                  </Box>
                )}
              </FlexLayout>
            </Card>
          </>
        )}
      </FlexLayout>
    </Section>
  );
}

const mapStateToProps = ({ workflow }) => ({
  creatorFullAccess: !!workflow.settings.permissions.isCreatorFullAccess,
  creatorPermissions: workflow.settings.permissions.creator,
  fullAccessPermissions: workflow.settings.permissions.fullAccess,
  stages: workflow.stages,
  wssDesignated: workflow.wssDesignated,
});

export default connect(mapStateToProps, {
  workflowSetCreatorFullAccess,
  workflowSetCreatorPermissions,
  workflowSetFullAccessPermissions,
})(Permissions);
